import {
	// Button,
	Card,
	Form,
	// Input,
	InputNumber,
	message,
	notification,
	Select,
	Switch,
	Tabs,
	Tag,
	Tooltip,
} from 'antd';

import { Toggle, Button, Input } from '@hydrodigital/design-system';

import React, { useContext, useEffect, useRef, useState } from 'react';
import VirtualSources from '../VirtualSources';
import { EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getModelFiles } from '../../../../../services/model';
import InpService from '../../../../../domain/ModelFile/InpService';
import SelectInpFile from '@/basicsComponents/Selects/SelectInpFile';
import SelectMetereological from '@/basicsComponents/Selects/SelectMetereological';
import { infraTypes } from '../../../../../statics/constants/infraTypes';
import InfraestructureContext from '@/contexts/InfraestructureContext';
import { ParameterSource } from '@/domain/ParameterSource/ParameterSource';
import { Infrastructure } from '@/domain/Infrastructure/Infrastructure';
import SelectAsset from '@/basicsComponents/Selects/SelectAsset';
import { useSelector, useDispatch } from 'react-redux';
import { selectUnitsByTypeId, getTypesWithUnitsAction } from '@/redux/slices/measurementTypesWithUnitsSlice';
import { Parameter } from '@/domain/Infrastructure/Parameter';
import AdjustCoordinatesModal from './AdjustCoordinatesModal';

const MOCK_DEFAULT_LATITUDE = 41.68338;
const MOCK_DEFAULT_LONGITUDE = -86.25001;

export interface IParameterSource extends ParameterSource {
	isEdit: boolean;
}

type VirtualSourceHandle = React.ElementRef<typeof VirtualSources>;

export type LatLonTarget = 'device' | 'sensor';

type Props = {
	source: IParameterSource;
	sources: ParameterSource[];
	infrastructure: Infrastructure;
	parameter: Parameter;
	updateSource: Function;
	deleteSourceById: Function;
	updateAndSaveSource: Function;
	onEditLatLon?: (target: LatLonTarget, source: IParameterSource) => void;
};

const inpService = new InpService();

function formatLatLon(latitude?: number | null, longitude?: number | null): string {
	if (latitude == null || longitude == null) {
		return '—';
	}
	return `${latitude}, ${longitude}`;
}

type DeviceSensorLatLonProps = {
	label: string;
	idValue: string;
	latitude?: number | null;
	longitude?: number | null;
	showCoordinates?: boolean;
	onEdit?: () => void;
};

function DeviceSensorLatLon({
	label,
	idValue,
	latitude,
	longitude,
	showCoordinates = true,
	onEdit,
}: DeviceSensorLatLonProps) {
	return (
		<div className="flex column">
			<span style={{ color: '#74788D', fontWeight: 400 }} className="mb-1">
				{label}
			</span>
			<span className="source-value mb-2">{idValue}</span>
			{showCoordinates && (
				<>
					<span style={{ color: '#74788D', fontWeight: 400 }} className="mb-1">
						Latitude, Longitude
					</span>
					<div className="flex items-center" style={{ columnGap: '8px' }}>
						<span className="source-value">{formatLatLon(latitude, longitude)}</span>
						<Button
							size="small"
							showIcon={true}
							showLabel={false}
							iconName="edit-alt"
							disabled={!onEdit}
							style={{
								borderWidth: 1,
								borderColor: '#74788D',
								borderRadius: '4px',
								color: '#74788D',
								height: 24,
								width: 24,
								padding: 0,
								backgroundColor: 'white',
							}}
							onClick={() => onEdit?.()}
						/>
					</div>
				</>
			)}
		</div>
	);
}

export default function SourceCard({
	source,
	sources,
	infrastructure,
	parameter,
	updateSource,
	deleteSourceById,
	updateAndSaveSource,
	onEditLatLon,
}: Props) {
	const [messageApi, contextHolder] = message.useMessage();
	const { value: context, setValue: setContext } = useContext(InfraestructureContext);
	const dispatch = useDispatch();
	const units = useSelector((state: any) => state.units.array);
	const types = useSelector((state: any) => state.types.array);
	const typesWithUnits = useSelector((state: any) => state.typesWithUnits.array);

	const virtualSourcesRef = React.useRef<VirtualSourceHandle>(null);
	const security = localStorage.getItem('security');
	const organizationId = JSON.parse(security || '{}').organizationId;
	const [inpId, setInpId] = useState<number>(0);
	const [inpObjectId, setInpObjectId] = useState<any>(null);
	const [inpObjectType, setInpObjectType] = useState<any>(null);
	const [virtualType, setVirtualType] = useState('CODE');
	const [codeValidationError, setCodeValidationError] = useState<string | null>(null);
	const [modelInfraIdList, setModelInfraIdList] = useState<any[]>([]);
	const [form] = Form.useForm();
	const [filterUnitsByType, setFilterUnitsByType] = useState<any[]>([]);
	const [isUnitCustom, setIsUnitCustom] = useState<boolean>(source.unitIsCustom || false);
	const [latLonTarget, setLatLonTarget] = useState<LatLonTarget | null>(null);
	const selectAssetRef = useRef<any>(null);

	const openLatLonModal = (target: LatLonTarget) => {
		if (onEditLatLon) {
			onEditLatLon(target, source);
			return;
		}
		setLatLonTarget(target);
	};

	const closeLatLonModal = () => setLatLonTarget(null);

	const saveLatLonModal = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
		if (!latLonTarget) return;
		const sourceCopy = { ...source };
		if (latLonTarget === 'device') {
			sourceCopy.deviceLatitude = latitude;
			sourceCopy.deviceLongitude = longitude;
		} else {
			sourceCopy.sensorLatitude = latitude;
			sourceCopy.sensorLongitude = longitude;
		}
		updateSource(sourceCopy);
		setLatLonTarget(null);
	};

	const modalInitialLat =
		latLonTarget === 'device'
			? (source.deviceLatitude ?? MOCK_DEFAULT_LATITUDE)
			: (source.sensorLatitude ?? MOCK_DEFAULT_LATITUDE);
	const modalInitialLng =
		latLonTarget === 'device'
			? (source.deviceLongitude ?? MOCK_DEFAULT_LONGITUDE)
			: (source.sensorLongitude ?? MOCK_DEFAULT_LONGITUDE);
	const modalTitle =
		latLonTarget === 'device' ? 'Adjust Device Coordinates' : 'Adjust Sensor Coordinates';

	const RAIN_GAUGE_MET_ID = 1;
	const MM_H_UNIT_ID = 25;

	const currentUnitId = source.unitId ?? parameter?.paramUnitId;
	const currentUnitInList = filterUnitsByType.some((u: any) => u.id === currentUnitId);
	const fallbackUnit =
		!currentUnitInList && currentUnitId != null
			? { id: currentUnitId, name: source.unitName || parameter?.parameterUnit || `Unit ${currentUnitId}` }
			: null;

	const mmhUnitExists = filterUnitsByType.some((unit: any) => unit.id === MM_H_UNIT_ID);

	const isUnitLockedByProvider =
		infrastructure.infraTypeId === infraTypes.PLUVIOMETER &&
		mmhUnitExists &&
		!!source.historicalMetService &&
		source.historicalMetService !== RAIN_GAUGE_MET_ID;

	useEffect(() => {
		if (typesWithUnits.length === 0) {
			dispatch(getTypesWithUnitsAction() as any);
		}
	}, []);

	useEffect(() => {
		if (parameter.paramTypeId && typesWithUnits.length > 0) {
			const unitsByType = selectUnitsByTypeId(typesWithUnits, parameter.paramTypeId || 0);
			setFilterUnitsByType(unitsByType);
		}
	}, [parameter.paramTypeId, typesWithUnits]);

	useEffect(() => {
		setIsUnitCustom(source.unitIsCustom || false);
	}, [source.unitIsCustom]);

	function replaceObjectById(array: any[], id: number, newObject: object) {
		for (let i = 0; i < array.length; i++) {
			if (array[i].id === id) {
				array[i] = newObject;
				break;
			}
		}
		return array;
	}

	async function handleSaveActionButton() {
		const sourceCopy = { ...source };
		sourceCopy.isEdit = false;
		if (!sourceCopy.name || sourceCopy.name == '') {
			notification.error({
				message: 'Error',
				description: `You must enter an Alert Name`,
			});
			return;
		}
		if (!sourceCopy.sourceSerialNumber || sourceCopy.sourceSerialNumber == '') {
			notification.error({
				message: 'Error',
				description: `You must enter an Alert Serial Number`,
			});
			return;
		}
		const [serialDeviceId, serialSensorId] = sourceCopy.sourceSerialNumber.split(':');
		if (serialSensorId === undefined || !serialDeviceId || !serialSensorId) {
			notification.error({
				message: 'Error',
				description: `Serial Number must follow the format DeviceId:SensorId`,
			});
			return;
		}
		if (infrastructure.infraTypeId == 8 && !sourceCopy.historicalMetService) {
			notification.error({
				message: 'Error',
				description: `You must select an meteostat service`,
			});
			return;
		}
		if (infrastructure.infraTypeId == infraTypes.PLUVIOMETER && sources.length > 0) {
			sourceCopy.forecastMetService = context.selectedParam?.sources[0].forecastMetService;
		}
		if (source.isVirtual) {
			sourceCopy.virtualType = virtualType;
			if (virtualType === 'CODE') {
				const codeValidationError = virtualSourcesRef.current?.validate?.();
				if (codeValidationError) {
					notification.error({ message: 'Validation Error', description: codeValidationError });
					return;
				}
				const virtual = virtualSourcesRef.current?.getVirtualSourceData();
				sourceCopy.statement = virtual.code;
				sourceCopy.variablesDTOS = virtual.variables;
				sourceCopy.virtualCurrentTs = virtualSourcesRef.current?.getDateStamp();
				sourceCopy.inpId = null;
				sourceCopy.objectTypeId = null;
				sourceCopy.objectModelId = null;
			} else {
				sourceCopy.inpId = form.getFieldValue('inpId');
				sourceCopy.objectTypeId = form.getFieldValue('inpObjectType');
				sourceCopy.objectModelId = form.getFieldValue('inpObjectId');
				sourceCopy.statement = null;
				sourceCopy.variablesDTOS = null;
				sourceCopy.virtualCurrentTs = null;
			}
		} else {
			sourceCopy.virtualType = '';
			sourceCopy.statement = null;
			sourceCopy.variablesDTOS = null;
			sourceCopy.virtualCurrentTs = null;
			sourceCopy.inpId = null;
			sourceCopy.objectTypeId = null;
			sourceCopy.objectModelId = null;
		}
		if (isUnitLockedByProvider) {
			sourceCopy.unitId = MM_H_UNIT_ID;
			sourceCopy.unitName = 'mm/h';
			sourceCopy.unitIsCustom = false;
			sourceCopy.unitScale = null;
			sourceCopy.unitOffset = null;
		} else if (form.getFieldValue('unitId') !== undefined && sourceCopy.unitId === null) {
			sourceCopy.unitId = null;
			sourceCopy.unitName = 'Custom';
			sourceCopy.unitOffset = Number(form.getFieldValue('unitOffset')) || 0;
			sourceCopy.unitScale = Number(form.getFieldValue('unitScale')) || 1;
			sourceCopy.unitIsCustom = true;
		} else if (sourceCopy.unitIsCustom && sourceCopy.unitId !== null) {
			sourceCopy.unitName = 'Custom';
			sourceCopy.unitOffset =
				Number(form.getFieldValue('unitOffset')) || sourceCopy.unitOffset || 0;
			sourceCopy.unitScale = Number(form.getFieldValue('unitScale')) || sourceCopy.unitScale || 1;
			sourceCopy.unitIsCustom = true;
		} else if (form.getFieldValue('unitId') === undefined) {
			sourceCopy.unitId = parameter.paramUnitId;
			sourceCopy.unitName = units.find((unit) => unit.id === parameter.paramUnitId)?.name;
			sourceCopy.unitIsCustom = false;
		} else {
			sourceCopy.unitId = form.getFieldValue('unitId');
			sourceCopy.unitName = form.getFieldValue('unitName');
			sourceCopy.unitIsCustom = false;
		}
		await updateAndSaveSource(sourceCopy);
	}

	useEffect(() => {
		if (source.id >= 0 && source.isVirtual && source.virtualType) {
			setVirtualType(source.virtualType);
			if (source.virtualType === 'MODEL') {
				form.setFieldValue('inpId', source.inpId);
				form.setFieldValue('inpObjectType', source.objectTypeId);
				form.setFieldValue('inpObjectId', source.objectModelId);

				setInpId(source.inpId as number);
				setInpObjectId(source.objectModelId);
				setInpObjectType(source.objectTypeId);
				if (source.unitId === null) {
					form.setFieldValue('unitId', null);
					form.setFieldValue('unitOffset', source.unitOffset);
					form.setFieldValue('unitScale', source.unitScale);
				} else if (source.unitId !== null) {
					form.setFieldValue('unitId', source.unitId);
					form.setFieldValue('unitName', source.unitName);
				} else {
					form.setFieldValue('unitId', null);
					form.setFieldValue('unitOffset', 0);
					form.setFieldValue('unitScale', 1);
				}
			}
		}
	}, [source]);

	return (
		<>
			{source.isEdit ? (
		<Card className="card-source-container w-full">
			<div className="flex justify-between items-center w-full" style={{ height: '50px' }}>
				<span style={{ color: '#232B36', fontWeight: '700' }}>
					<Input
						size="small"
						style={{ width: '240px' }}
						maxLength={100}
						placeholder="Type the source name"
						defaultValue={source.name}
						onBlur={(e) => {
							const sourceCopy = { ...source };
							sourceCopy.name = e.target.value;
							updateSource(sourceCopy);
						}}
					/>
				</span>
				<div style={{ display: 'flex' }}>
					{/* <Button
						type="primary"
						className="flex items-center justify-center"
						style={{
							backgroundColor: '#023E65',
							color: 'white',
							width: '42px',
							height: '16px',
							fontWeight: '700',
							cursor: 'pointer',
							marginRight: '10px',
						}}
						onClick={() => {
							if (source.isVirtual && virtualType === 'MODEL') {
								form
									.validateFields()
									.then(() => {
										handleSaveActionButton();
									})
									.catch((error) => {
										// console.log(error)
									});
							} else {
								handleSaveActionButton();
							}
						}}
					>
						Save
					</Button> */}

					<div className="ml-2">
						<Button
							label="Save"
							showIcon={false}
							size="small"
							disabled={source.isVirtual && virtualType === 'CODE' && !!codeValidationError}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: source.isVirtual && virtualType === 'CODE' && !!codeValidationError ? '#d9d9d9' : '#023E65',
								color: source.isVirtual && virtualType === 'CODE' && !!codeValidationError ? '#a0a0a0' : 'white',
								cursor: source.isVirtual && virtualType === 'CODE' && !!codeValidationError ? 'not-allowed' : 'pointer',
							}}
							onClick={() => {
								if (source.isVirtual && virtualType === 'MODEL') {
									form
										.validateFields()
										.then(() => {
											handleSaveActionButton();
										})
										.catch((error) => {
											// console.log(error)
										});
								} else {
									handleSaveActionButton();
								}
							}}
						/>
					</div>

					{/* <DeleteOutlined
            style={{
              cursor: "pointer",
              fontSize: "1.5rem",
              color: "#74788D",
            }}
            onClick={() => {}}
            rev={undefined}
          /> */}
				</div>
			</div>

			<div className="separator" />
			<div
				className="flex items-center"
				style={{
					minHeight: '50px',
					columnGap: '12px',
				}}
			>
				<span
					style={{
						color: '#74788D',
						fontWeight: '400',
						minWidth: '110px',
					}}
				>
					Serial Number
				</span>
				<Input
					size="small"
					style={{ flex: 1 }}
					maxLength={100}
					placeholder="DeviceId:SensorId"
					defaultValue={source.sourceSerialNumber}
					onBlur={(e) => {
						setTimeout(() => {
							const serialNumber = e.target.value;
							const [serialDeviceId, serialSensorId] = serialNumber.split(':');
							const sourceCopy = { ...source };
							sourceCopy.sourceSerialNumber = serialNumber;
							sourceCopy.deviceId = serialDeviceId ?? null;
							sourceCopy.sensorId = serialSensorId ?? null;
							sourceCopy.deviceLatitude = sourceCopy.deviceLatitude ?? infrastructure.latitude;
							sourceCopy.deviceLongitude = sourceCopy.deviceLongitude ?? infrastructure.longitude;
							sourceCopy.sensorLatitude = sourceCopy.sensorLatitude ?? infrastructure.latitude;
							sourceCopy.sensorLongitude = sourceCopy.sensorLongitude ?? infrastructure.longitude;
							updateSource(sourceCopy);
						}, 100);
					}}
				/>
			</div>
			<div
				className="grid py-3"
				style={{
					gridTemplateColumns: '1fr 1fr',
					columnGap: '12px',
				}}
			>
				<DeviceSensorLatLon
					label="Device ID"
					idValue={source.deviceId || '—'}
					latitude={source.deviceLatitude}
					longitude={source.deviceLongitude}
					showCoordinates={!source.isVirtual}
					onEdit={() => openLatLonModal('device')}
				/>
				<DeviceSensorLatLon
					label="Sensor ID"
					idValue={source.sensorId || '—'}
					latitude={source.sensorLatitude}
					longitude={source.sensorLongitude}
					showCoordinates={!source.isVirtual}
					onEdit={() => openLatLonModal('sensor')}
				/>
			</div>
			<div className="separator" />
			<div className="flex gap-3 py-3">
				<span
					style={{
						color: '#74788D',
						fontWeight: '400',
					}}
					className="mb-1"
				>
					Unit
				</span>
				<div className="flex flex-col items-start gap-2">
					<Select
						style={{ width: '120px' }}
						disabled={isUnitLockedByProvider}
						value={
							isUnitLockedByProvider
								? 25
								: source.unitIsCustom
									? 'custom'
									: source.unitId !== null
										? source.unitId
										: parameter
											? parameter.paramUnitId
											: 'custom'
						}
						placeholder="Select an Unit"
						onChange={(value) => {
							const isCustom = value === 'custom';
							const unitId = isCustom ? null : value;

							let unitName = 'Custom';
							if (!isCustom && value) {
								const selectedUnit = filterUnitsByType.find((unit) => unit.id === value);
								if (selectedUnit) {
									unitName = selectedUnit.name;
								}
							}

							setIsUnitCustom(isCustom);

							form.setFieldValue('unitId', unitId);
							form.setFieldValue('unitName', unitName);
							form.setFieldValue('unitIsCustom', isCustom);

							const updatedSource = {
								...source,
								unitId: unitId,
								unitName: unitName,
								unitIsCustom: isCustom,
								unitTypeId: parameter.paramTypeId,
								...(isCustom
									? { unitScale: source.unitScale || 0, unitOffset: source.unitOffset || 0 }
									: {}),
							};
							updateSource(updatedSource);
						}}
					>
						{fallbackUnit && (
							<Select.Option key={fallbackUnit.id} value={fallbackUnit.id}>
								{fallbackUnit.name}
							</Select.Option>
						)}
						{filterUnitsByType?.map((unit: any) => (
							<Select.Option key={unit.id} value={unit.id}>
								{unit.name}
							</Select.Option>
						))}
						<Select.Option value="custom">Custom</Select.Option>
					</Select>
					{(isUnitCustom || source.unitIsCustom === true) && (
						<div className="flex items-center gap-2">
							<div className="flex flex-col">
								<span>Scale</span>

								<InputNumber
									placeholder="0.0"
									value={source.unitScale || 1}
									onChange={(value) => {
										const numValue = Number(value) || 1;
										form.setFieldValue('unitScale', numValue);
										const sourceCopy = { ...source, unitScale: numValue };
										updateSource(sourceCopy);
									}}
								/>
							</div>
							<div className="flex flex-col">
								<span>Offset</span>

								<InputNumber
									placeholder="0.0"
									value={source.unitOffset || 0}
									onChange={(value) => {
										const numValue = Number(value) || 0;
										form.setFieldValue('unitOffset', numValue);
										const sourceCopy = { ...source, unitOffset: numValue };
										updateSource(sourceCopy);
									}}
								/>
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="separator" />
			<div className="flex justify-between items-center" style={{ height: '40px' }}>
				<div>
					<span className="mr-1">Enable</span>
					<Toggle
						checkedText="ON"
						uncheckedText="OFF"
						size="small"
						defaultChecked={source.isEnabled}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							const sourceCopy = { ...source };
							sourceCopy.isEnabled = e.target.checked;
							updateSource(sourceCopy);
						}}
					/>
				</div>
				{infrastructure.infraTypeId != 8 ? (
					<div>
						<span className="mr-1">Virtual</span>
						<Toggle
							checkedText="ON"
							uncheckedText="OFF"
							size="small"
							defaultChecked={source.isVirtual}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const sourceCopy = { ...source };
								sourceCopy.isVirtual = e.target.checked;
								updateSource(sourceCopy);
							}}
						/>
					</div>
				) : (
					<div className="flex items-center">
						<span className="mr-1">
							Source <span style={{ color: 'red' }}>*</span>
						</span>
						<div style={{ width: '140px' }}>
							<SelectMetereological
								mode="historical"
								defaultValue={source.historicalMetService}
								onSelect={(value: number | null | undefined) => {
									const sourceCopy = { ...source };
									sourceCopy.historicalMetService = value;
									const shouldLockToMmh =
										mmhUnitExists && !!value && value !== RAIN_GAUGE_MET_ID;
									if (shouldLockToMmh) {
										sourceCopy.unitId = MM_H_UNIT_ID;
										sourceCopy.unitName = 'mm/h';
										sourceCopy.unitIsCustom = false;
										sourceCopy.unitScale = null;
										sourceCopy.unitOffset = null;
										setIsUnitCustom(false);
										form.setFieldValue('unitId', MM_H_UNIT_ID);
										form.setFieldValue('unitName', 'mm/h');
									}
									updateSource(sourceCopy);
								}}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="separator mb-2" />
			{source.isVirtual && (
				<div className="flex column">
					<Tag icon={<InfoCircleOutlined style={{ fontWeight: 800 }} />} color="processing">
						<span style={{ fontWeight: 700 }}>Use subindex to reference to past values</span>
					</Tag>
					<Tabs
						activeKey={virtualType}
						onChange={(val) => {
							setVirtualType(val);
						}}
						items={[
							{
								key: 'CODE',
								label: 'Python Code',
								children: (
									<VirtualSources
										ref={virtualSourcesRef}
										source={source}
										sources={sources}
										infrastructure={infrastructure}
										onValidationChange={setCodeValidationError}
									/>
								),
							},
							{
								key: 'MODEL',
								label: 'Model',
								children: (
									<Form form={form} layout="vertical" autoComplete="off" size="small">
										<div className="flex column mt-2">
											<Form.Item
												label="Model File (id - name)"
												name="inpId"
												rules={[
													{
														required: true,
														message: 'Please select an option',
													},
													{
														validator: (_, value) =>
															value !== 0
																? Promise.resolve()
																: Promise.reject(new Error('Please select an option')),
													},
												]}
											>
												<SelectInpFile
													defaultValue={inpId}
													onSelect={(val: any) => {
														setInpObjectId(null);
														setInpId((_) => val);
														form.setFieldValue('inpObjectId', null);
														selectAssetRef.current?.setValue(null);
														form.setFieldValue('inpId', val);
													}}
													organizationId={organizationId}
													onlyCompleted={true}
													locationId={infrastructure.locationId}
												/>
											</Form.Item>

											<Form.Item
												label="Object Type"
												name="inpObjectType"
												rules={[
													{
														required: true,
														message: 'Please select a Object Type',
													},
													{
														validator: (_, value) =>
															value !== 0
																? Promise.resolve()
																: Promise.reject(new Error('Please select an option')),
													},
												]}
											>
												<Select
													style={{ width: '100%' }}
													defaultValue={inpObjectType}
													placeholder="Select an option"
													onChange={(val) => {
														setInpObjectType(val);
														setInpObjectId(null);
														selectAssetRef.current?.setValue(null);
														form.setFieldValue('inpObjectId', null);
													}}
													options={[
														{ value: 1, label: 'Nodes' },
														{ value: 2, label: 'Links' },
													]}
												/>
											</Form.Item>
											<Form.Item
												label="Model Asset ID"
												name="inpObjectId"
												rules={[
													{
														required: true,
														message: 'Please enter a Model Asset ID',
													},
													{
														validator: (_, value) =>
															value !== 0
																? Promise.resolve()
																: Promise.reject(new Error('Please select an option')),
													},
												]}
											>
												<SelectAsset
													ref={selectAssetRef}
													inpId={inpId}
													typeId={inpObjectType}
													defaultValue={inpObjectId}
													onSelect={(val: any) => {
														form.setFieldValue('inpObjectId', val);
														setInpObjectId(val);
													}}
												/>
											</Form.Item>
										</div>
									</Form>
								),
							},
						]}
					/>
				</div>
			)}
		</Card>
	) : (
		<Card className="card-source-container">
			<div className="flex justify-between items-center" style={{ height: '50px' }}>
				<span style={{ color: '#232B36', fontWeight: '700' }}>{source.name}</span>
				<div className="flex">
					<Tooltip title="Edit">
						{/* <Button
							icon={<EditOutlined rev={undefined} />}
							style={{
								color: '#74788D',
								marginRight: '10px',
							}}
							onClick={() => {
								const sourceCopy = { ...source };
								sourceCopy.isEdit = true;
								updateSource(sourceCopy);
							}}
						/> */}

						<Button
							size="small"
							showIcon={true}
							showLabel={false}
							iconName="edit-alt"
							style={{
								borderWidth: 1,
								borderColor: '#74788D',
								borderRadius: '4px',
								color: '#74788D',
								height: 24,
								width: 24,
								padding: 0,
								backgroundColor: 'white',
							}}
							onClick={() => {
								const sourceCopy = { ...source };
								sourceCopy.isEdit = true;
								updateSource(sourceCopy);
							}}
						/>
					</Tooltip>
					<Tooltip title="Delete">
						{/* <Button
							icon={<DeleteOutlined rev={undefined} />}
							style={{
								color: '#74788D',
								marginRight: '10px',
							}}
							onClick={async () => {
								await deleteSourceById(source.id);
								// handleSaveSources();
							}}
						/> */}
						<div className="ml-1">
							<Button
								size="small"
								showIcon={true}
								showLabel={false}
								iconName="trash-alt"
								style={{
									borderWidth: 1,
									borderColor: '#74788D',
									borderRadius: '4px',
									color: '#74788D',
									height: 24,
									width: 24,
									padding: 0,
									backgroundColor: 'white',
								}}
								onClick={async () => {
									await deleteSourceById(source.id);
									// handleSaveSources();
								}}
							/>
						</div>
					</Tooltip>
				</div>
			</div>

			<div className="separator" />
			<div
				className="flex items-center"
				style={{ minHeight: '50px', columnGap: '12px' }}
			>
				<span
					style={{ color: '#74788D', fontWeight: 400, minWidth: '110px' }}
				>
					Serial Number
				</span>
				<span className="source-value">{source.sourceSerialNumber}</span>
			</div>
			<div className="separator" />
			<div className="flex gap-3 py-3">
				<span
					style={{
						color: '#74788D',
						fontWeight: '400',
					}}
					className="mb-1"
				>
					Unit
				</span>
				<div className="flex flex-col gap-2">
					<span className="font-semibold">{source.unitName}</span>
					{(isUnitCustom || source.unitIsCustom === true) && (
						<div className="flex gap-2">
							<span className="text-[#74788D]">Scale:</span>
							<span className="font-semibold">{source.unitScale}</span>
							<span className="text-[#74788D]">Offset:</span>
							<span className="font-semibold">{source.unitOffset}</span>
						</div>
					)}
				</div>
			</div>
			<div className="separator" />
			<div className="flex justify-between items-center" style={{ height: '40px' }}>
				<div>
					<span className="mr-1">Enable</span>
					<Toggle checkedText="ON" uncheckedText="OFF" size="small" defaultChecked={source.isEnabled} disabled />
				</div>
				{infrastructure.infraTypeId != 8 ? (
					<div>
						<span className="mr-1">Virtual</span>
						<Toggle checkedText="ON" uncheckedText="OFF" size="small" defaultChecked={source.isVirtual} disabled />
					</div>
				) : (
					<div className="flex items-center">
						<span className="mr-1">
							Source <span style={{ color: 'red' }}>*</span>
						</span>
						<div style={{ width: '140px' }}>
							<SelectMetereological
								mode="historical"
								defaultValue={source.historicalMetService}
								otherSettings={{ disabled: true }}
								onSelect={() => {}}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="flex" style={{ height: '40px' }}>
				<div>
					<span style={{ color: '#023E65', fontSize: 15, fontWeight: 5 }}>ID: {source.id} </span>
				</div>
			</div>
		</Card>
			)}
			<AdjustCoordinatesModal
				isOpen={latLonTarget !== null}
				title={modalTitle}
				initialLatitude={modalInitialLat}
				initialLongitude={modalInitialLng}
				onSave={saveLatLonModal}
				onCancel={closeLatLonModal}
			/>
		</>
	);
}
